import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { notification } from 'antd';
import { useHistory } from 'react-router-dom';
import { t } from '../i18n/i18n';
import {
  setCurrentWorkspace,
  setWorkspaceFavorite,
  setWorkspacesList,
  setUser,
  setCurrentRole,
  setPlan,
  setPlanFeatures,
} from '../redux/actions/authActions';
import { HOME, CONFIGURATION, CREATED_WORKSPACE } from '../constants/routes';
import { setFavoriteWorkspace, createWorkspaceAsync } from '../API/services/screeningService';
import { getMe } from '../API/services/authService';
import { PartnerWorkspaceRole } from '../constants/enums';
import { getPlanByWorkspaceId, getPlanFeatures } from '../API/services/alkymersService';
import { setSelectedSearch } from '../redux/actions/screeningActions';

export const useWorkspaceManager = () => {
  const { user, workspacesList, currentWorkspace } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const history = useHistory();

  const [workspaceInfo, setWorkspaceInfo] = useState({
    placeholder: '',
    color: '',
    icon: undefined,
    iconId: undefined,
  });
  const [workspaceName, setWorkspaceName] = useState('');
  const [searchWorkspace, setSearchWorkspace] = useState('');
  const [showModalAddWorkspace, setShowModalAddWorkspace] = useState(false);
  const [createWorkspaceLoader, setCreateWorkspaceLoader] = useState(false);
  const [worskpacesListFilter, setWorkspacesListFilter] = useState([]);

  useEffect(() => {
    if (workspacesList?.length) {
      const workspace =
        workspacesList.find((workspace) => Number(workspace.id) === Number(currentWorkspace)) ??
        workspacesList[0];
      setWorkspaceInfo({
        placeholder: workspace?.name ?? '',
        color: workspace?.color ?? '',
        icon: workspace?.icon ?? '',
        iconId: workspace?.iconId ?? undefined,
      });
      setWorkspacesListFilter(workspacesList);
    } else if (user?.workspaces?.length) {
      const workspaceData = user.workspaces.map((w) => ({
        id: w.workspaceId,
        favorite: w.favorite,
        color: w.workspace.color,
        name: w.workspace.name,
        icon: w.workspace.icon,
        iconId: w.workspace.iconId,
        createdAt: w.workspace.createdAt,
        updatedAt: w.workspace.updatedAt,
        companyId: w.workspace.companyId,
        companyName: w.companyName,
        companyLogo: w.companyLogo,
        companyPrimaryColor: w.companyPrimaryColor,
        role: w.role,
      }));
      dispatch(setWorkspacesList(workspaceData));
    }
  }, [currentWorkspace, workspacesList, user, dispatch]);

  const setWorkspace = async (workspaceId) => {
    dispatch(setCurrentWorkspace(workspaceId));
    dispatch(
      setCurrentRole(
        user.workspaces.filter((workspace) => workspace.workspaceId === workspaceId)[0]?.role
      )
    );
    dispatch(setSelectedSearch({}));

    try {
      const planData = await getPlanByWorkspaceId(workspaceId);
      dispatch(setPlan(planData));

      if (planData?.subscription?.subscriptionPending?.planId) {
        const featuresData = await getPlanFeatures(
          planData.subscription.subscriptionPending.planId
        );
        dispatch(setPlanFeatures(featuresData));
      }
    } catch (error) {
      notification.open({
        message: t('PLANS_LOAD_ERROR'),
        type: 'error',
        duration: 3,
      });
    }

    if (history.location.pathname !== HOME) {
      history.push(HOME);
    }
  };

  useEffect(() => {
    if (searchWorkspace) {
      setWorkspacesListFilter(
        workspacesList.filter((workspace) =>
          workspace.name.toLowerCase().includes(searchWorkspace.toLowerCase())
        )
      );
    } else {
      setWorkspacesListFilter(workspacesList);
    }
  }, [searchWorkspace, workspacesList]);

  const addFavorite = async (id) => {
    await setFavoriteWorkspace({
      id,
      favorite: true,
    })
      .then(() => {
        dispatch(
          setWorkspacesList(
            workspacesList?.map((workspace) => {
              if (workspace.id === id) {
                return { ...workspace, favorite: true };
              }
              return workspace;
            })
          )
        );
        dispatch(setWorkspaceFavorite(`${id}-add`));
      })
      .catch(() => {
        notification.open({
          message: t('SET_FAVORITE_WORKSPACE_ERROR'),
          type: 'error',
          duration: 3,
        });
      });
  };

  const removeFavorite = async (id) => {
    await setFavoriteWorkspace({
      id,
      favorite: false,
    })
      .then(() => {
        dispatch(
          setWorkspacesList(
            workspacesList?.map((workspace) => {
              if (workspace.id === id) {
                return { ...workspace, favorite: false };
              }
              return workspace;
            })
          )
        );
        dispatch(setWorkspaceFavorite(`${id}-remove`));
      })
      .catch(() => {
        notification.open({
          message: t('SET_FAVORITE_WORKSPACE_ERROR'),
          type: 'error',
          duration: 3,
        });
      });
  };

  const handleCreateWorkspace = async () => {
    const workspaceInfoData = {
      name: workspaceName.trim(),
      companyId: user?.companyId,
    };
    setCreateWorkspaceLoader(true);
    const response = await createWorkspaceAsync(workspaceInfoData);
    if (response) {
      const workspace = {
        id: response.workspaceId,
        favorite: false,
        color: '',
        name: workspaceName.trim(),
        icon: '',
        iconId: null,
        role: 'OWNER',
        companyId: user.companyId,
      };
      const userData = await getMe();
      dispatch(setUser(userData));
      dispatch(setCurrentWorkspace(response.workspaceId));
      dispatch(setCurrentRole(PartnerWorkspaceRole.OWNER));
      dispatch(setWorkspacesList([...workspacesList, workspace]));
      setShowModalAddWorkspace(false);
      setWorkspaceName('');
      setCreateWorkspaceLoader(false);
      const res = await getPlanByWorkspaceId(response.workspaceId);
      dispatch(setPlan(res));
      const featureRes = await getPlanFeatures(res.subscription.subscriptionPending.planId);
      dispatch(setPlanFeatures(featureRes));
      history.push(CREATED_WORKSPACE);
    } else {
      setShowModalAddWorkspace(false);
      setCreateWorkspaceLoader(false);
      setWorkspaceName('');
      notification.open({
        message: t('ERROR_TO_CREATE_WORKSPACE'),
        type: 'error',
      });
    }
  };

  const createWorkspace = () => {
    setShowModalAddWorkspace(true);
  };

  const redirectToConfig = () => {
    history.push(CONFIGURATION);
  };

  useEffect(() => {
    setWorkspacesListFilter(workspacesList);
  }, [workspacesList]);

  return {
    workspaceInfo,
    workspacesList,
    worskpacesListFilter,
    searchWorkspace,
    showModalAddWorkspace,
    createWorkspaceLoader,
    workspaceName,
    currentWorkspace,
    setSearchWorkspace,
    setWorkspace,
    addFavorite,
    removeFavorite,
    createWorkspace,
    redirectToConfig,
    handleCreateWorkspace,
    setShowModalAddWorkspace,
    setWorkspaceName,
  };
};
